import { useEffect, useEffectEvent, useState, type RefObject } from 'react'

// rects are fractional, so an item that exactly fills the row can measure just past it
const SUBPIXEL_TOLERANCE = 0.5

// measures how many of the container's items fit in a nav row, and the width the
// container needs to hold them. the container shares the row with items that always
// render, and the ones after it keep their space, so it only gets what is left. the
// container's own width is never read, so a measurement can't be skewed by the previous one.
export const useNavOverflow = ({
  rowRef,
  containerRef,
  itemCount,
  onChange,
}: {
  rowRef: RefObject<HTMLElement | null>
  containerRef: RefObject<HTMLElement | null>
  itemCount: number
  onChange: (visibleCount: number) => void
}) => {
  const [visibleWidth, setVisibleWidth] = useState(0)

  const recalculate = useEffectEvent(() => {
    const container = containerRef.current
    const row = rowRef.current
    if (!container || !row) return

    // the container may be nested, so measure from the row child that holds it
    const rowItems = [...row.children]
    const containerIndex = rowItems.findIndex((item) => item.contains(container))
    if (containerIndex === -1) return

    const trailingWidth = rowItems
      .slice(containerIndex + 1)
      .reduce((total, item) => total + item.getBoundingClientRect().width, 0)

    // the container's left edge is set by what comes before it, so it
    // holds no matter how wide the container ends up
    const available =
      row.getBoundingClientRect().right -
      container.getBoundingClientRect().left -
      trailingWidth +
      SUBPIXEL_TOLERANCE

    let visibleCount = 0
    let width = 0
    for (const item of container.children) {
      const nextWidth = width + item.getBoundingClientRect().width
      if (nextWidth > available) break
      visibleCount++
      width = nextWidth
    }

    // pinning the container to the items that fit keeps "More" against the last one
    setVisibleWidth(width)
    onChange(visibleCount)
  })

  useEffect(() => {
    const container = containerRef.current
    const row = rowRef.current
    if (!container || !row || !itemCount) return

    recalculate()

    const observer = new ResizeObserver(() => {
      recalculate()
    })
    observer.observe(container)
    observer.observe(row)

    return () => {
      observer.disconnect()
    }
  }, [containerRef, rowRef, itemCount])

  return visibleWidth
}
